import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserRole } from '@klaro7/shared-types';

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import { useSpecializations } from '@/features/professionals/hooks/usePublicProfessionals';
import { getWallet } from '@/features/wallet/services/wallet.service';
import { getConversations } from '@/features/chat/services/chat.service';

const MAX_VISIBLE_CATEGORIES = 5;

export function PlatformNavbar() {
  const { isAuthenticated, user, logout, hasRole, openLoginModal } = useAuth();
  const { data: registrationStatus } = useRegistrationStatus();
  const registrationOpen = registrationStatus?.registrationEnabled ?? false;
  const { data: specializations } = useSpecializations();
  const { data: wallet } = useQuery({
    queryKey: ['wallet'],
    queryFn: getWallet,
    enabled: isAuthenticated,
  });
  const { data: chatConversations } = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: getConversations,
    enabled: isAuthenticated,
    staleTime: 1000 * 30,
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchValue, setSearchValue] = useState(searchParams.get('search') ?? '');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [altroMenuOpen, setAltroMenuOpen] = useState(false);
  const [visibleCategoryCount, setVisibleCategoryCount] = useState(MAX_VISIBLE_CATEGORIES);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const altroMenuRef = useRef<HTMLDivElement>(null);
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const categoryMeasureRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (altroMenuRef.current && !altroMenuRef.current.contains(e.target as Node)) {
        setAltroMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchValue.trim();
    if (trimmed) {
      navigate(`/consulenti?search=${encodeURIComponent(trimmed)}`);
    } else {
      navigate('/consulenti');
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const availableCategories = useMemo(
    () => (specializations ?? []).filter((s) => s.count > 0),
    [specializations]
  );
  const desktopVisibleCategoryCount = Math.min(
    visibleCategoryCount,
    MAX_VISIBLE_CATEGORIES,
    availableCategories.length
  );
  const visibleCategories = availableCategories.slice(0, desktopVisibleCategoryCount);
  const overflowCategories = availableCategories.slice(desktopVisibleCategoryCount);
  const isAdminOrPro = hasRole([UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.PROFESSIONAL]);

  const userInitial = user?.profile?.firstName?.[0] ?? user?.email?.charAt(0).toUpperCase() ?? '?';

  const totalUnread = chatConversations?.reduce((sum, conv) => {
    const isCurrentUserTheUser = user?.id === String(conv.userId);
    return sum + (isCurrentUserTheUser ? conv.unreadCountUser : conv.unreadCountProfessional);
  }, 0) ?? 0;

  useLayoutEffect(() => {
    const container = categoriesContainerRef.current;
    const measure = categoryMeasureRef.current;
    if (!container || !measure) return;

    const updateVisibleCategories = () => {
      const maxCandidates = Math.min(MAX_VISIBLE_CATEGORIES, availableCategories.length);
      if (maxCandidates === 0) {
        setVisibleCategoryCount(0);
        return;
      }

      const categoryWidths = Array.from(
        measure.querySelectorAll<HTMLElement>('[data-category-measure]')
      ).map((element) => element.offsetWidth);
      const overflowWidth = measure.querySelector<HTMLElement>('[data-overflow-measure]')?.offsetWidth ?? 0;
      const gap = parseFloat(getComputedStyle(container).columnGap || '0') || 0;
      const availableWidth = container.clientWidth;

      let nextVisibleCount = 0;
      for (let count = maxCandidates; count >= 0; count -= 1) {
        const hasOverflow = count < availableCategories.length;
        const visibleWidth = categoryWidths
          .slice(0, count)
          .reduce((total, width) => total + width, 0);
        const displayedItems = count + (hasOverflow ? 1 : 0);
        const requiredWidth =
          visibleWidth +
          (hasOverflow ? overflowWidth : 0) +
          Math.max(0, displayedItems - 1) * gap;

        if (requiredWidth <= availableWidth) {
          nextVisibleCount = count;
          break;
        }
      }

      setVisibleCategoryCount((current) => (
        current === nextVisibleCount ? current : nextVisibleCount
      ));
    };

    updateVisibleCategories();

    const resizeObserver = new ResizeObserver(updateVisibleCategories);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [availableCategories]);

  return (
    <>
    <nav className="sticky top-0 z-50 border-b border-white/10 bg-brand-navy/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4">
        {/* Logo */}
        <Link to="/" className="shrink-0">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-11 w-auto sm:h-12 lg:h-14" />
        </Link>

        {/* Desktop categories */}
        <div
          ref={categoriesContainerRef}
          className="relative hidden min-w-0 flex-1 items-center gap-1 lg:flex"
        >
          {visibleCategories.map((spec) => (
            <Link
              key={spec.name}
              to={`/consulenti?specialization=${encodeURIComponent(spec.name)}`}
              className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                searchParams.get('specialization') === spec.name
                  ? 'bg-brand-orange/15 text-brand-orange'
                  : 'text-white/60 hover:bg-white/5 hover:text-white/80'
              }`}
            >
              {spec.name}
            </Link>
          ))}
          {overflowCategories.length > 0 && (
            <div className="relative shrink-0" ref={altroMenuRef}>
              <button
                onClick={() => setAltroMenuOpen(!altroMenuOpen)}
                className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white/80"
              >
                Altro
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {altroMenuOpen && (
                <div className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-white/10 bg-brand-navy p-1 shadow-xl">
                  {overflowCategories.map((spec) => (
                    <Link
                      key={spec.name}
                      to={`/consulenti?specialization=${encodeURIComponent(spec.name)}`}
                      onClick={() => setAltroMenuOpen(false)}
                      className="block rounded-md px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      {spec.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
          <div
            ref={categoryMeasureRef}
            aria-hidden="true"
            className="pointer-events-none invisible absolute left-0 top-0 flex h-0 gap-1 overflow-hidden whitespace-nowrap"
          >
            {availableCategories.map((spec) => (
              <span
                key={spec.name}
                data-category-measure
                className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium"
              >
                {spec.name}
              </span>
            ))}
            <span
              data-overflow-measure
              className="flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium"
            >
              Altro
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>

        {/* Desktop search */}
        <form onSubmit={handleSearch} className="hidden w-80 min-w-[7rem] shrink lg:flex">
          <div className="relative w-full">
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Cerca consulente..."
              className="h-9 w-full rounded-lg border border-white bg-white/5 pl-9 pr-3 text-sm text-white placeholder-white/60 outline-none transition-colors focus:border-white focus:bg-white/[0.07]"
            />
          </div>
        </form>

        {/* Spacer */}
        <div className="flex-1 lg:hidden" />

        {/* Desktop right side */}
        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          {isAuthenticated && user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-sm text-white transition-colors hover:bg-white/10"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-teal to-brand-teal-dark text-xs font-bold text-white">
                  {userInitial}
                </span>
                <span className="max-w-[120px] truncate">
                  {user.profile?.firstName ?? user.email}
                </span>
                <svg className="h-3.5 w-3.5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 min-w-[220px] rounded-lg border border-white/10 bg-brand-navy p-1 shadow-xl">
                  <Link
                    to="/my/consultations"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
                    </svg>
                    Le Mie Consulenze
                  </Link>
                  <Link
                    to="/my/wallet"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3" />
                    </svg>
                    Borsellino
                    {wallet?.balance != null && (
                      <span className="ml-auto text-xs font-semibold text-brand-orange">
                        {((wallet.bonusBalance || 0) + wallet.balance).toFixed(2)}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my/promozioni"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                    </svg>
                    Le mie Promozioni
                  </Link>
                  <Link
                    to="/my/preferiti"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                    I miei Preferiti
                  </Link>
                  <Link
                    to="/my/chat"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Chat
                    {totalUnread > 0 && (
                      <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-orange px-1.5 text-[10px] font-bold text-white">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my/supporto"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                    </svg>
                    Assistenza
                  </Link>
                  <Link
                    to="/my/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Profilo
                  </Link>

                  {isAdminOrPro && (
                    <>
                      <div className="mx-2 my-1 border-t border-white/10" />
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                        </svg>
                        Dashboard Admin
                      </Link>
                    </>
                  )}

                  <div className="mx-2 my-1 border-t border-white/10" />
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-red-400/70 transition-colors hover:bg-white/5 hover:text-red-400"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                    </svg>
                    Esci
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => openLoginModal('/my/supporto', 'support')}
                className="flex items-center gap-1.5 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-brand-navy transition-colors hover:bg-brand-sky-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal/60"
              >
                <svg aria-hidden="true" className="h-4 w-4 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
                Assistenza
              </button>
              <button
                onClick={() => openLoginModal()}
                className="rounded-lg px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
              >
                Accedi
              </button>
              {registrationOpen && (
                <Link
                  to="/register"
                  className="rounded-lg border border-brand-orange/40 bg-brand-orange/10 px-4 py-2 text-sm font-medium text-brand-orange transition-all hover:bg-brand-orange/20 hover:border-brand-orange/60"
                >
                  Registrati
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="rounded-lg p-2.5 text-white/70 transition-colors hover:bg-white/5 lg:hidden"
          aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
        >
          {mobileMenuOpen ? (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile categories horizontal scroll */}
      <div className="no-scrollbar flex gap-2 overflow-x-auto border-t border-white/5 px-4 py-2 lg:hidden">
        <Link
          to="/"
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
            !searchParams.get('specialization')
              ? 'bg-brand-orange/15 text-brand-orange'
              : 'bg-white/5 text-white/50'
          }`}
        >
          Tutti
        </Link>
        {availableCategories.map((spec) => (
          <Link
            key={spec.name}
            to={`/?specialization=${encodeURIComponent(spec.name)}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              searchParams.get('specialization') === spec.name
                ? 'bg-brand-orange/15 text-brand-orange'
                : 'bg-white/5 text-white/50'
            }`}
          >
            {spec.name}
          </Link>
        ))}
      </div>

    </nav>

      {/* Mobile slide-out panel — portaled to body so it's above all page content */}
      {mobileMenuOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[90] bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 z-[91] w-72 overflow-y-auto bg-brand-navy shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
              <span className="text-sm font-semibold text-white">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-lg p-2 text-white/50 hover:bg-white/5"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              {/* Mobile search */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <svg
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Cerca consulente..."
                    className="h-10 w-full rounded-lg border border-white bg-white/5 pl-9 pr-3 text-sm text-white placeholder-white/60 outline-none focus:border-white"
                  />
                </div>
              </form>

              {/* Mobile user menu items */}
              {isAuthenticated && user ? (
                <div className="space-y-1">
                  <div className="mb-3 flex items-center gap-3 rounded-lg bg-white/5 px-3 py-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-teal to-brand-teal-dark text-sm font-bold text-white">
                      {userInitial}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-white">
                        {user.profile?.firstName} {user.profile?.lastName}
                      </p>
                      <p className="truncate text-xs text-white/40">{user.email}</p>
                    </div>
                  </div>

                  <Link
                    to="/my/consultations"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Le Mie Consulenze
                  </Link>
                  <Link
                    to="/my/wallet"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Borsellino
                    {wallet?.balance != null && (
                      <span className="text-xs font-semibold text-brand-orange">
                        {((wallet.bonusBalance || 0) + wallet.balance).toFixed(2)}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my/promozioni"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Le mie Promozioni
                  </Link>
                  <Link
                    to="/my/preferiti"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    I miei Preferiti
                  </Link>
                  <Link
                    to="/my/chat"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Chat
                    {totalUnread > 0 && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-orange px-1.5 text-[10px] font-bold text-white">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my/supporto"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Assistenza
                  </Link>
                  <Link
                    to="/my/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    Profilo
                  </Link>

                  {isAdminOrPro && (
                    <>
                      <div className="my-2 border-t border-white/10" />
                      <Link
                        to="/dashboard"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block rounded-lg px-3 py-3.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                      >
                        Dashboard Admin
                      </Link>
                    </>
                  )}

                  <div className="my-2 border-t border-white/10" />
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full rounded-lg px-3 py-3.5 text-left text-sm text-red-400/70 transition-colors hover:bg-white/5 hover:text-red-400"
                  >
                    Esci
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="px-1 pb-1 text-xs font-medium text-white/40">Hai bisogno di aiuto?</p>
                  <button
                    onClick={() => { setMobileMenuOpen(false); openLoginModal('/my/supporto', 'support'); }}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-sky px-4 py-3 text-center text-sm font-semibold text-brand-navy transition-colors hover:bg-brand-sky-dark"
                  >
                    <svg aria-hidden="true" className="h-4 w-4 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                    </svg>
                    Assistenza
                  </button>
                  <div className="my-1 border-t border-white/10" />
                  <button
                    onClick={() => { setMobileMenuOpen(false); openLoginModal(); }}
                    className="w-full rounded-lg bg-white/5 px-4 py-3 text-center text-sm font-medium text-white transition-colors hover:bg-white/10"
                  >
                    Accedi
                  </button>
                  {registrationOpen && (
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block rounded-lg border border-brand-orange/40 bg-brand-orange/10 px-4 py-3 text-center text-sm font-medium text-brand-orange transition-colors hover:bg-brand-orange/20"
                    >
                      Registrati
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
